import React, { PropsWithChildren, useEffect, useState } from 'react'
import { UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { LinkDetailType } from 'service/list'
import { ReactComponent as YoutubeIcon } from 'svg/fi_youtube.svg'
import { FormValues } from 'utils/type'
import classNames from 'classnames'

import { YoutubeVideo } from './YoutubeVideo'
import { VideoFileUploader } from './VideoFileUploader'
import { VideoPlayer } from './VideoPlayer'
import { CreateVideoType } from 'constant/videoTypes'

interface ProductVideoSelectorProps {
  selectedLink: LinkDetailType | null
  index: number
  setValue: UseFormSetValue<FormValues>
  watch: UseFormWatch<FormValues>
  createVideoType: CreateVideoType
}
export const ProductVideoSelector = ({
  selectedLink,
  index,
  setValue,
  watch,
  createVideoType,
}: PropsWithChildren<ProductVideoSelectorProps>) => {
  const [selectedVideos, setSelectedVideos] = useState<
    { order: number; videoUrl: string }[]
  >([])

  const updateVideos = (videoUrl: string) => {
    const found = selectedVideos.find((video) => video.videoUrl === videoUrl)

    if (found) {
      // 선택 해제
      const filtered = selectedVideos
        .filter((video) => video.videoUrl !== videoUrl)
        .map((video, idx) => ({ ...video, order: idx + 1 }))

      setSelectedVideos(filtered)
      setValue(
        `products.${index}.selectedVideos`,
        filtered.map((i) => i.videoUrl)
      )
    } else {
      const updated = [
        ...selectedVideos,
        { order: selectedVideos.length + 1, videoUrl: videoUrl },
      ]
      setSelectedVideos(updated)
      setValue(
        `products.${index}.selectedVideos`,
        updated.map((i) => i.videoUrl)
      )
    }
  }

  /**
   * YouTube URL에서 동영상 ID를 추출합니다.
   * 지원되는 URL 예시:
   *  - https://www.youtube.com/watch?v=VIDEO_ID
   *  - https://youtu.be/VIDEO_ID
   */
  function extractYouTubeId(url: string): string | null {
    /**
     * YouTube URL에서 동영상 ID를 추출합니다.
     * 지원되는 URL 예시:
     *   - https://www.youtube.com/watch?v=VIDEO_ID (ID 길이가 1~11자)
     *   - https://youtu.be/VIDEO_ID
     *   - https://www.youtube.com/embed/VIDEO_ID?params...
     *   - https://www.youtube.com/embed/?params&playlist=VIDEO_ID
     */
    const regex =
      /(?<=v=)([0-9A-Za-z_-]{1,11})|(?<=youtu\.be\/)([0-9A-Za-z_-]{1,11})|(?<=embed\/)([0-9A-Za-z_-]{1,11})|(?<=playlist=)([0-9A-Za-z_-]{1,11})/
    const match = url.match(regex)

    return match ? match[1] || match[2] || match[3] || match[4] || null : null
  }

  useEffect(() => {
    const videos = selectedLink?.video?.videos

    if (videos && videos?.length > 0) {
      const initSelectedAllVideo = videos.map((video, index) => {
        return {
          order: index + 1,
          videoUrl: video,
        }
      })

      setSelectedVideos(initSelectedAllVideo)
      setValue(
        `products.${index}.selectedVideos`,
        initSelectedAllVideo.map((i) => i.videoUrl)
      )
    }
  }, [selectedLink])

  return (
    <div className="flex flex-col w-full mt-6">
      <p className="w-full font-bold flex gap-2 items-center">
        <YoutubeIcon />
        상품 영상 선택
      </p>

      {(selectedLink?.video?.videos.length ?? 0) > 0 && (
        <>
          {' '}
          <div
            className={classNames(
              'bg-[#F6F8FB] w-full   rounded-[10px] grid md:grid-cols-8 grid-cols-3 p-4 gap-x-[7px] gap-y-1'
            )}
          >
            {selectedLink?.video?.videos?.map((videoUrl, el_index) => {
              const selected = selectedVideos.find(
                (video) => video.videoUrl === videoUrl
              )
              const order = selected?.order ?? 0
              return (
                <>
                  <div
                    className={classNames(
                      'w-[85px] relative flex items-center',
                      {
                        'min-h-[110px]':
                          createVideoType !== CreateVideoType.PRODUCT_SHORT,
                        'min-h-[190px]':
                          createVideoType === CreateVideoType.PRODUCT_SHORT &&
                          !videoUrl.includes('www.youtube.com'),
                      }
                    )}
                  >
                    {' '}
                    {videoUrl.includes('www.youtube.com') ? (
                      <>
                        {' '}
                        <a href={videoUrl} target="_blank" rel="noreferrer">
                          <YoutubeVideo
                            onClick={() => {
                              updateVideos(videoUrl)
                            }}
                            videoId={extractYouTubeId(videoUrl) ?? ''}
                          />
                        </a>
                      </>
                    ) : (
                      <>
                        {' '}
                        <a href={videoUrl} target="_blank" rel="noreferrer">
                          <VideoPlayer
                            onClick={() => {
                              updateVideos(videoUrl)
                            }}
                            path={videoUrl}
                            createVideoType={createVideoType}
                          />
                        </a>
                      </>
                    )}
                    <div
                      className="absolute flex items-center cursor-pointer right-1 bottom-8 z-50 active:scale-95"
                      onClick={() => {
                        updateVideos(videoUrl)
                      }}
                    >
                      <div
                        className={classNames(
                          'w-[28px] h-[28px] rounded-full border-2 flex items-center justify-center  border-href ',
                          {
                            'opacity-100 bg-white ': order > 0,
                            'bg-[rgba(0,0,0,0.5)] border-white ': order <= 0,
                          }
                        )}
                      >
                        {order > 0 && (
                          <div className="w-[22px] h-[22px] bg-href rounded-full text-white flex items-center justify-center font-bold">
                            {order}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )
            })}
          </div>
        </>
      )}
      <VideoFileUploader
        watch={watch}
        index={index}
        setValue={setValue}
        setSelectedVideos={setSelectedVideos}
        selectedVideos={selectedVideos}
        createVideoType={createVideoType}
      />
    </div>
  )
}
